import type { PromotionBlockCode, PromotionPreflight, PromotionRepoPreflight, PromotionTarget } from '@/types/api';
import {
  ANCESTRY_REASON,
  PREFLIGHT_REPO_STATUS,
  PROMOTION_BLOCK,
  PROMOTION_TARGET,
} from '@/lib/copy';
import { formatClock, shortSha } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Flag, StateChip } from '@/components/state-chip';
import { CopyValue } from '@/components/ui/copy-value';
import { LedgerGlyph, type GlyphKind } from '@/components/ledger/glyph';

/* ---------------------------------------------------------------------------
   THE PRE-FLIGHT REPORT.

   Phase 1 of a promotion writes nothing, and its job is to name the repo that
   will stop the merge before any merge happens. So this component is built
   around naming things:

     * every blocking reason at once, never one per round trip;
     * the conflicting PATHS per repo, because "a conflict somewhere in three
       repos" is not an actionable sentence;
     * for production, the per-repo ancestry check with its reason — including
       the fail-closed case, a repo with no recorded staging merge at all, which
       blocks the whole promotion on its own.

   The repos are drawn on the SAME rail geometry as a run, in position order,
   because that is the order the merges will run in. A reviewer who has learned
   to read one rail reads this one.
--------------------------------------------------------------------------- */

function glyphFor(repo: PromotionRepoPreflight): GlyphKind {
  switch (repo.status) {
    case 'clean':
      return 'succeeded';
    case 'conflict':
    case 'blocked':
      return 'failed';
    case 'no_changes':
      return 'skipped';
    default:
      return 'pending';
  }
}

export function PreflightReport({
  preflight,
  target,
  className,
}: {
  preflight: PromotionPreflight;
  target: PromotionTarget;
  className?: string;
}) {
  const repos = [...preflight.repos].sort((a, b) => a.position - b.position || a.repo_id - b.repo_id);
  const reasons = preflight.blocked_reasons ?? [];

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-sm text-ink">
          {preflight.can_promote ? (
            <>
              Pre-flight is clean. {PROMOTION_TARGET[target].verb} will merge{' '}
              <span className="font-mono">{repos.filter((r) => r.status === 'clean').length}</span> of{' '}
              <span className="font-mono">{repos.length}</span> repos in position order.
            </>
          ) : (
            <>Refused. Nothing has been merged, and nothing will be until every reason below clears.</>
          )}
        </p>
        {preflight.computed_at ? (
          <span className="font-mono text-tiny text-ink-3">pre-flighted {formatClock(preflight.computed_at)}</span>
        ) : null}
      </div>

      {reasons.length > 0 ? <BlockedReasons reasons={reasons} /> : null}

      <ol className="relative m-0 list-none p-0">
        <span aria-hidden className="pointer-events-none absolute bottom-2 left-[13px] top-2 w-px bg-rule" />
        {repos.map((repo) => (
          <RepoPreflightStop key={repo.repo_id} repo={repo} target={target} />
        ))}
      </ol>

      {preflight.preflight_token ? (
        <p className="border-t border-rule pt-2 text-xs leading-[17px] text-ink-3">
          The pre-flight token pins the exact per-repo SHAs above. The merge re-reads each target ref and compares before
          writing, so a push that lands in the meantime is refused with a{' '}
          <span className="font-mono">preflight_stale</span> rather than merged silently. If that happens, re-run
          pre-flight — never retry with the old token.
        </p>
      ) : (
        <p className="border-t border-rule pt-2 font-mono text-tiny text-warn">
          No pre-flight token was issued, so no merge can be started from this result.
        </p>
      )}
    </div>
  );
}

function BlockedReasons({ reasons }: { reasons: PromotionBlockCode[] }) {
  return (
    <ul className="m-0 list-none space-y-1.5 border border-danger bg-danger-soft p-2.5 pl-2.5">
      {reasons.map((code) => {
        const copy = PROMOTION_BLOCK[code];
        return (
          <li key={code} className="flex gap-2">
            <span aria-hidden className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-[1px] bg-danger" />
            <div className="min-w-0">
              <p className="flex flex-wrap items-baseline gap-x-2 text-sm font-medium text-ink">
                {copy.label}
                <span className="font-mono text-tiny text-ink-3">{code}</span>
              </p>
              {copy.detail ? <p className="mt-0.5 text-xs leading-[17px] text-ink-2">{copy.detail}</p> : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function RepoPreflightStop({ repo, target }: { repo: PromotionRepoPreflight; target: PromotionTarget }) {
  const state = PREFLIGHT_REPO_STATUS[repo.status];
  const conflicts = repo.conflict_paths ?? [];
  const ancestry = repo.ancestry ?? null;
  const protectionMissing = repo.protection_enforced === false;

  return (
    <li className="relative pb-3 pl-7 last:pb-0">
      <span className="absolute left-[2.5px] top-[3px] block">
        <LedgerGlyph kind={glyphFor(repo)} />
      </span>

      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <span className="font-mono text-xs text-ink">{repo.repo_slug}</span>
        <span className="font-mono text-tiny text-ink-3">#{repo.position}</span>
        <StateChip size="sm" state={state} />
        {protectionMissing ? (
          <Flag
            label="unprotected"
            tone="danger"
            title="Branch protection is not enforced on this repo, so platform Go code is the only thing standing between arbitrary code and the target branch. That blocks production on its own."
          />
        ) : null}
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-tiny text-ink-3">
        <span>
          {shortSha(repo.target_sha)} ← {shortSha(repo.source_sha)}
        </span>
        {repo.source_sha ? <CopyValue className="text-tiny" value={repo.source_sha} display="copy source sha" /> : null}
      </div>

      {conflicts.length > 0 ? (
        <div className="mt-2 border-l-2 border-danger bg-danger-soft px-2.5 py-2">
          <p className="eyebrow text-danger">Conflicting paths</p>
          <ul className="m-0 mt-1 list-none space-y-0.5 p-0">
            {conflicts.map((path) => (
              <li key={path} className="break-all font-mono text-tiny text-ink">
                {path}
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-xs leading-[17px] text-ink-2">
            A conflict in any repo refuses the whole promotion with nothing merged. Resolve it on{' '}
            <span className="font-mono">pre-staging</span> — a run in this thread is the normal way — then pre-flight
            again.
          </p>
        </div>
      ) : null}

      {target === 'production' ? <AncestryRow ancestry={ancestry} /> : null}
    </li>
  );
}

/**
 * The production gate, per repo.
 *
 * It is computed in git and it fails closed: a repo with no recorded staging
 * merge blocks the whole promotion. It is deliberately NOT a status check, and
 * saying so here is the point — an expert who thinks `merged to staging` is the
 * gate will not understand why production is refused on a chat whose status says
 * exactly that.
 */
function AncestryRow({ ancestry }: { ancestry: { passed: boolean; recorded_staging_sha?: string | null; staging_head_sha?: string | null; reason?: 'ok' | 'no_recorded_merge' | 'not_ancestor' | 'ref_missing' | null } | null }) {
  if (!ancestry) {
    return (
      <p className="mt-1.5 font-mono text-tiny text-warn">
        No ancestry result for this repo. The gate fails closed, so this blocks production.
      </p>
    );
  }

  const reason = ancestry.reason ?? (ancestry.passed ? 'ok' : 'no_recorded_merge');

  return (
    <div
      className={cn(
        'mt-1.5 border-l-2 px-2.5 py-1.5',
        ancestry.passed ? 'border-ok bg-ok-soft' : 'border-danger bg-danger-soft',
      )}
    >
      <p className={cn('eyebrow', ancestry.passed ? 'text-ok' : 'text-danger')}>
        Staging ancestry {ancestry.passed ? 'verified' : 'failed'}
      </p>
      <p className="mt-0.5 text-xs leading-[17px] text-ink-2">{ANCESTRY_REASON[reason]}</p>
      <p className="mt-1 font-mono text-tiny text-ink-3">
        recorded {shortSha(ancestry.recorded_staging_sha)} · staging head {shortSha(ancestry.staging_head_sha)}
      </p>
    </div>
  );
}
